import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, useToast } from '@morressier/alembic';
import { faClockFour } from '@fortawesome/pro-light-svg-icons';
import { useRequestDeadlineExtension } from 'src/api/commands';
import { useAssessmentData } from 'src/contexts/AssessmentDataProvider';
import { toUTCDateString } from '../../../../../utils/date';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getExtensionSchema } from '@publishing-engine/domain/validation';
import { FormFieldDate } from '../../../../FormField';
import { getMaxUTCDateString } from '@publishing-engine/domain/utils';

interface IRequestReviewDeadlineExtensionModalProps {
  submissionId: string;
  submissionContextId: string;
  onClose: () => void;
}

export const RequestAssessmentDeadlineExtensionModal = ({
  onClose
}: IRequestReviewDeadlineExtensionModalProps) => {
  const toast = useToast();
  const {
    workflowResponse,
    refetchAssessment,
    earlyAssessment,
    organizationId
  } = useAssessmentData();

  const currentDeadline = toUTCDateString(
    workflowResponse.workflowData.currentDeadline ?? ''
  );
  const maxDate = getMaxUTCDateString(currentDeadline);

  const methods = useForm({
    defaultValues: {
      proposedDate: ''
    },
    resolver: zodResolver(getExtensionSchema(currentDeadline))
  });

  const requestDeadlineExtension = useRequestDeadlineExtension({
    organizationId,
    submissionId: earlyAssessment?.caseData.submission.id ?? '',
    reviewRequestId: earlyAssessment?.id ?? '',
    submissionContextId: earlyAssessment?.submissionContextId ?? ''
  });

  const deadlineExtensions = earlyAssessment?.workflow.deadlineExtensions;

  const onSubmit = async () => {
    if (deadlineExtensions?.status === 'requested') {
      toast({
        variant: 'error',
        title: 'Deadline extension already requested.',
        description: 'Please wait until the extension is approved.',
        isClosable: true
      });
      return;
    }

    const response = await requestDeadlineExtension.mutateAsync(
      methods.getValues().proposedDate
    );
    await refetchAssessment();
    const remainingDeadlineAutoExtension =
      workflowResponse.workflowData.deadlineExtensions?.remainingAutoextensions;

    let toastTitle = '';
    let toastDescription = '';
    if (remainingDeadlineAutoExtension) {
      toastTitle = 'Extension Granted';
      toastDescription =
        remainingDeadlineAutoExtension !== 1
          ? 'Your request for extension has been automatically granted.'
          : 'Your request for extension has been automatically granted. Further requests will need Editor’s approval.';
    } else {
      toastTitle = 'Extension Requested';
      toastDescription = 'Your request was sent to this Journal’s Editors.';
    }

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: toastTitle,
        description: toastDescription,
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: 'Error requesting extension',
        description: 'Please try again later.',
        isClosable: true
      });
    }
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      width="28rem"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Flex flexDirection="column" gap={8} padding={8}>
            <Flex>
              <FontAwesomeIcon fontSize={32} icon={faClockFour} />
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Request Extension?</Text>
              <Text textStyle="textBody">
                This extension request will be reviewed by the editorial team,
                and you will be notified via email of their decision. If your
                extension request is approved, your deadline will be updated,
                and you will be notified of this change.
              </Text>
              <FormFieldDate
                config={{
                  name: 'proposedDate',
                  component: 'Custom:DateInput',
                  caseDataFields: {
                    main: 'proposedDate'
                  },
                  params: {
                    labels: {
                      main: 'Proposed Deadline Date',
                      subtitle: ''
                    },
                    validations: {
                      kind: 'date',
                      required: true,
                      min: currentDeadline,
                      max: maxDate
                    }
                  }
                }}
              />
            </Flex>
            <Flex justifyContent="flex-end" gap={2}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Request Extension
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
